import { useEffect } from 'react';
import { useSeopEdit } from '@/contexts/SeopEditContext';

/**
 * Avisa antes de perder edição não salva.
 *
 * Duas camadas, porque nenhuma cobre a outra:
 *
 * - **Sair do navegador** (reload, fechar aba): `beforeunload`. É o único gancho
 *   que existe para isso, e o navegador ignora mensagem customizada.
 * - **Navegar dentro do app** (abas do módulo, seletor de mês): o estado sobe
 *   pelo `SeopEditContext` e o `SeopLayout` intercepta o clique.
 *
 * O `useBlocker` do react-router **não serve aqui**: ele chama
 * `useDataRouterContext`, que lança quando o contexto é nulo — e o contexto só
 * existe sob `createBrowserRouter`. O app monta `<BrowserRouter>` em 40+ rotas;
 * migrar o router para habilitar um hook é troca desproporcional.
 */
export function useUnsavedChanges(dirty: boolean, save?: () => Promise<void>): void {
  const { setDirty, registerSave } = useSeopEdit();

  useEffect(() => {
    setDirty(dirty);
    return () => setDirty(false);
  }, [dirty, setDirty]);

  useEffect(() => {
    registerSave(save ?? null);
    return () => registerSave(null);
  }, [save, registerSave]);

  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Navegadores modernos ignoram o texto, mas `returnValue` ainda é o que
      // dispara o diálogo nativo.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);
}

export default useUnsavedChanges;
